import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  DELOITTE_OFFERING_FULL_DISPLAY_NAME_MAP,
  INDUSTRY_OPTIONS,
  OFFERING_OPTIONS,
  PRACTICE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
} from '@freelancer/datastore/collections';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { HoverColor, IconColor, IconSize } from '@freelancer/ui/icon';
import { InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { DeloitteOfferingApi } from 'api-typings/projects/deloitte';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-hire-me-deloitte-project-details',
  template: `
    <fl-hr [flMarginBottom]="Margin.SMALL"></fl-hr>
    <fl-bit class="ProjectDetailsHeadingWrapper">
      <fl-heading
        i18n="Project Details title"
        [color]="HeadingColor.LIGHT"
        [headingType]="HeadingType.H3"
        [size]="TextSize.SMALL"
        [flMarginBottom]="Margin.XSMALL"
      >
        Project Details
      </fl-heading>
      <fl-button
        flTrackingLabel="expandBillingDetails"
        (click)="handleExpand()"
      >
        <fl-icon
          *ngIf="expanded"
          name="ui-chevron-down"
          label="Collapse"
          i18n-label="Project details collapse icon"
          [color]="IconColor.MID"
          [size]="IconSize.SMALL"
          [hoverColor]="HoverColor.PRIMARY"
        ></fl-icon>
        <fl-icon
          *ngIf="!expanded"
          name="ui-chevron-right"
          label="Expand"
          i18n-label="Projects details expand icon"
          [color]="IconColor.MID"
          [size]="IconSize.SMALL"
          [hoverColor]="HoverColor.PRIMARY"
        ></fl-icon>
      </fl-button>
    </fl-bit>
    <fl-bit *ngIf="expanded" [ngClass]="{ ResponsiveContainer: isResponsive }">
      <fl-bit class="ResponsiveField">
        <fl-text
          i18n="Hire Me project type label"
          [weight]="FontWeight.BOLD"
          [color]="FontColor.LIGHT"
        >
          Project type
        </fl-text>
        <fl-select
          i18n-placeholder="Project Type"
          flTrackingLabel="ProjectType"
          placeholder="Please select project type"
          [flMarginBottom]="Margin.XSMALL"
          [control]="hireMeFormGroup.get('projectType')"
          [options]="PROJECT_TYPE_OPTIONS"
        ></fl-select>
      </fl-bit>
      <fl-bit class="ResponsiveField">
        <fl-text
          i18n="Hire Me project practice label"
          [weight]="FontWeight.BOLD"
          [color]="FontColor.LIGHT"
        >
          Practice
        </fl-text>
        <fl-select
          i18n-placeholder="Project Practice"
          flTrackingLabel="ProjectPractice"
          placeholder="Please select project practice"
          [flMarginBottom]="Margin.XSMALL"
          [control]="hireMeFormGroup.get('projectPractice')"
          [options]="PRACTICE_OPTIONS"
        ></fl-select>
      </fl-bit>
      <fl-bit class="ResponsiveField">
        <fl-text
          i18n="Hire Me project industry label"
          [weight]="FontWeight.BOLD"
          [color]="FontColor.LIGHT"
        >
          Industry
        </fl-text>
        <fl-select
          i18n-placeholder="Project Industry"
          flTrackingLabel="ProjectIndustry"
          placeholder="Please select project industry"
          [flMarginBottom]="Margin.XSMALL"
          [control]="hireMeFormGroup.get('projectIndustry')"
          [options]="INDUSTRY_OPTIONS"
        ></fl-select>
      </fl-bit>
      <fl-bit class="ResponsiveField">
        <fl-bit class="PortfolioWrapper">
          <fl-text
            i18n="Hire Me project offering label"
            [weight]="FontWeight.BOLD"
            [color]="FontColor.LIGHT"
          >
            Offering
          </fl-text>
          <fl-text
            *ngIf="selectedParent$ | async as selectedParent"
            i18n="Offering portfolio"
            [color]="FontColor.LIGHT"
            [flMarginBottom]="Margin.XXXSMALL"
          >
            {{ selectedParent }}
          </fl-text>
        </fl-bit>
        <fl-select
          i18n-placeholder="Select your offering"
          flTrackingLabel="SelectOffering"
          placeholder="Please select project offering"
          [flMarginBottom]="Margin.XSMALL"
          [control]="hireMeFormGroup.get('projectOffering')"
          [options]="OFFERING_OPTIONS"
        ></fl-select>
      </fl-bit>
      <fl-bit class="ResponsiveField">
        <fl-bit [flMarginBottom]="Margin.XSMALL">
          <fl-checkbox
            i18n-label="Is clearance required label"
            label="Is clearance required for this project?"
            flTrackingLabel="isClearanceRequiredCheckbox"
            [control]="hireMeFormGroup.get('isClearanceRequired')"
          >
          </fl-checkbox>
        </fl-bit>
        <fl-input
          placeholder="Specify clearance"
          i18n-placeholder="specify project clearance"
          flTrackingLabel="isClearanceRequiredInput"
          [flMarginBottom]="Margin.XSMALL"
          [control]="hireMeFormGroup.get('clearance')"
          [disabled]="hireMeFormGroup.get('clearance')?.disabled || undefined"
        ></fl-input>
      </fl-bit>
      <fl-bit class="ResponsiveField" [flMarginBottom]="Margin.MID">
        <fl-checkbox
          i18n-label="Is project subject to ITAR label"
          label="
              Is this project subject to the International Traffic in Arms Regulations (ITAR)?
            "
          flTrackingLabel="isSubjectToItarCheckbox"
          [control]="hireMeFormGroup.get('isSubjectToItar')"
        >
        </fl-checkbox>
      </fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./hire-me-deloitte-project-details.component.scss'],
})
export class HireMeDeloitteProjectDetailsComponent implements OnInit {
  TextSize = TextSize;
  HeadingType = HeadingType;
  HeadingColor = HeadingColor;
  FontColor = FontColor;
  FontWeight = FontWeight;
  InputType = InputType;
  Margin = Margin;
  IconColor = IconColor;
  IconSize = IconSize;
  HoverColor = HoverColor;
  PROJECT_TYPE_OPTIONS = PROJECT_TYPE_OPTIONS;
  PRACTICE_OPTIONS = PRACTICE_OPTIONS;
  INDUSTRY_OPTIONS = INDUSTRY_OPTIONS;
  OFFERING_OPTIONS = OFFERING_OPTIONS;

  expanded = true;

  @Input() isResponsive = false;
  @Input() hireMeFormGroup: FormGroup;
  selectedParent$: Rx.Observable<string | undefined>;

  ngOnInit() {
    this.selectedParent$ = this.hireMeFormGroup.controls.projectOffering.valueChanges.pipe(
      map(
        (offering: DeloitteOfferingApi) =>
          DELOITTE_OFFERING_FULL_DISPLAY_NAME_MAP[offering] &&
          DELOITTE_OFFERING_FULL_DISPLAY_NAME_MAP[offering].split(',')[0],
      ),
    );
  }

  handleExpand() {
    this.expanded = !this.expanded;
  }
}
